import React from 'react';
import { useField, useFormikContext, ErrorMessage } from 'formik';
import classNames from 'classnames';
import DatePicker, { ReactDatePickerProps } from 'react-datepicker';
import getUserLocale from 'get-user-locale';

interface FormDateTimeFieldProps {
    fieldName: string;
    label: string;
    readOnly?: boolean;
    required?: boolean;
    datePickerProps?: Omit<ReactDatePickerProps, 'onChange'>;
    onChange?: (date: Date | null) => void;
    tooltipIconData?: { mainIcon: string; closeIcon: string };
    tooltip?: string;
    renderTooltipTextAsHtml?: boolean;
    customClassName?: string;
}

const FormDateTimeField: React.FC<FormDateTimeFieldProps> = ({
    fieldName,
    label,
    readOnly = false,
    required = false,
    datePickerProps = {},
    onChange,
    tooltipIconData,
    tooltip,
    renderTooltipTextAsHtml = false,
    customClassName,
}) => {
    const validate = required
        ? (value: string) => (!value?.trim() ? `${label} is required.` : undefined)
        : undefined;

    const [field, meta] = useField({ name: fieldName, validate });
    const formik = useFormikContext();
    const hasError = !!(meta.touched && meta.error);

    const handleChange = (date: Date | null) => {
        formik.setFieldValue(fieldName, date);
        if (onChange) {
            onChange(date);
        }
    };

    const selectedDate = field.value ? new Date(field.value) : null;
    const locale = getUserLocale();

    return (
        <div
            className={classNames(
                `${
                    customClassName
                        ? customClassName + ' FormBox FormSideBySide'
                        : 'FormBox FormSideBySide'
                }`,
                {
                    FormFieldRequired: required,
                }
            )}
        >
            <div className="FormLabel">
                <label htmlFor={fieldName}>{label}</label>
                {tooltip && tooltipIconData && (
                    <div className="Tooltip">
                        <img src={tooltipIconData.mainIcon} alt="Tooltip" />
                        <span>{tooltip}</span>
                    </div>
                )}
            </div>
            <div className="FormField">
                <DatePicker
                    id={fieldName}
                    dateFormat="P"
                    locale={locale}
                    className={classNames(
                        'EditBox',
                        { ViewOnly: readOnly },
                        { ErrorInput: hasError }
                    )}
                    onChange={handleChange}
                    selected={selectedDate}
                    readOnly={readOnly}
                    placeholderText={readOnly ? '' : 'Select date...'}
                    {...datePickerProps}
                />
                <ErrorMessage name={fieldName} component="div" className="ErrorLabel" />
            </div>
        </div>
    );
};

export default FormDateTimeField;
